import React from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";

import t from "@constants/localization";
import Text from "@components/CustomText";
import Button from "./Button";
import ShowLimitedImages from "./ShowOrderImages";
import RtoFooter from "./RtoFooter";
import DeliveredFooter from "./DeliveredFooter";
import color from "@constants/color";
import Reload from "@assets/svg/Reload";
import Cash from "@assets/svg/RRD/Cash";
import { useSelector } from "react-redux";
import IssueFooter from "./IssueFooter";

interface ButtonInfo {
  key: string;
  value: string;
  color: "primary" | "secondary";
}

interface Footer {
  text?: string;
  actionKey?: string;
}

interface OrderItem {
  id: string;
  image: string;
}

interface DeliveryCardItem {
  card_id: string;
  title?: string;
  order_items?: OrderItem[];
  amount_to_collect?: number;
  amount_to_collect_txt?: string;
  amount_collected?: number;
  amount_collected_txt?: string;
  card_type: "RTO_CAN_CANCEL" | "RTO_CANNOT_CANCEL" | "DELIVERED" | string;
  footer?: Footer;
  buttonsInfo?: ButtonInfo[];
  order_ids?: number[];
  order_items_count?: number;
  is_escalated?: boolean;
}

interface InnerDeliveryCardProps {
  item: DeliveryCardItem;
  onPressBtn: (key: string, item: DeliveryCardItem) => void;
  orderMarkingMap?: any;
  onMarkingOrder?: any;
  cardMarkingBtnRef?: any;
  changeMarkingEnabled?: boolean;
}

const InnerDeliveryCard: React.FC<InnerDeliveryCardProps> = ({
  item,
  onPressBtn,
  orderMarkingMap,
  onMarkingOrder,
  cardMarkingBtnRef,
  changeMarkingEnabled = true,
}) => {
  const PATH = "screens/MyTask";
  const onChangeMarking = (order_id) => {
    onMarkingOrder(order_id, null);
  };
  const { multiOrderDelivery = false } = useSelector(
    ({ configReducer }) => configReducer,
  );
  const showMarkingChange = (orderMarking) => {
    if (orderMarking) {
      return (
        orderMarking?.key === "CONFIRM_DELIVERY" ||
        orderMarking?.key === "CANCEL_DELIVERY" ||
        orderMarking?.key === "ALL_ISSUES"
      );
    }
    return false;
  };

  const getTagStyle = (key: string, showMarking: boolean = true) => {
    if (key === "CANCEL_DELIVERY") {
      return {
        backgroundColor: "rgba(253, 226, 226, 1)",
        textColor: color.red,
        tagText: t(`${PATH}/customer_rto`),
        showMarkingChange: showMarking,
      };
    } else if (key === "CONFIRM_DELIVERY") {
      return {
        backgroundColor: "rgba(236, 252, 229, 1)",
        textColor: color.darkGreen,
        tagText: t(`${PATH}/ready_to_delivery`),
        showMarkingChange: showMarking,
      };
    } else if (key === "ALL_ISSUES") {
      return {
        backgroundColor: "rgba(253, 243, 226, 1)",
        textColor: "rgba(255, 142, 77, 1)",
        tagText: "All Issues",
        showMarkingChange: showMarking,
      };
    }
    return {
      backgroundColor: "rgba(253, 226, 226, 1)",
      textColor: color.darkGreen,
      tagText: "",
      showMarkingChange: showMarking,
    };
  };
  return (
    <View style={styles.listView} key={`${item.card_id}${item?.title}`}>
      <View style={styles.cardHeaderContainer}>
        <View style={styles.quantityContainer}>
          <Text variant="body_text_2" style={styles.quantityText}>
            {t(`${PATH}/MarkingModal/total_quantity`) +
              ` ${item?.order_items_count}`}
          </Text>
        </View>

        <Text variant="caption_2" style={styles.titleText}>
          {item?.title}
        </Text>
      </View>
      <View style={styles.imagesContainer}>
        {item?.order_items?.length > 0 && (
          <ShowLimitedImages imgs={item?.order_items} />
        )}
      </View>
      {item?.amount_to_collect_txt && !multiOrderDelivery ? (
        <View style={styles.amountOverviewContainer}>
          <Cash pay={Math.sign(item?.amount_to_collect || 0) === 1} />
          <Text variant="body_text_1" style={styles.amountLabel}>
            {t(
              `${PATH}/${
                Math.sign(item?.amount_to_collect || 0) === 1
                  ? "pay_by_cx"
                  : "pay_cx"
              }`,
            )}
            <Text variant="body_text_2" style={styles.amountText}>
              {item?.amount_to_collect_txt}
            </Text>
          </Text>
        </View>
      ) : null}
      {item?.amount_collected_txt && !multiOrderDelivery ? (
        <View
          style={[
            styles.amountOverviewContainer,
            styles.amountCollectedContainer,
          ]}
        >
          <Text variant="body_text_1" style={styles.amountLabel}>
            {t(`${PATH}/amount_collected`)}
            <Text variant="body_text_2" style={styles.amountText}>
              {item?.amount_collected_txt}
            </Text>
          </Text>
        </View>
      ) : null}
      {(item?.card_type === "RTO_CAN_CANCEL" ||
        item?.card_type === "RTO_CANNOT_CANCEL") && (
        <>
          <View style={styles.spacer12} />
          <RtoFooter footer={item?.footer} />
        </>
      )}
      {item?.card_type === "ALL_ISSUES" && (
        <>
          <View style={styles.spacer12} />
          <IssueFooter footer={item?.footer} />
        </>
      )}
      {item?.card_type === "DELIVERED" && (
        <>
          <View style={[styles.divider, styles.dividerMargin]} />
          <DeliveredFooter footer={item?.footer} />
        </>
      )}
      {item?.card_type !== "DELIVERED" && (
        <View style={styles.bottomButtonContainer}>
          {showMarkingChange(orderMarkingMap[item?.order_ids[0]]) ? (
            <View
              style={[
                styles.buttonContainer,
                { justifyContent: "space-between" },
              ]}
            >
              {item?.card_type === "ALL_ISSUES" ? (
                <View style={styles.spacer12} />
              ) : (
                <View
                  style={[
                    styles.issueBtn,
                    {
                      backgroundColor: getTagStyle(
                        orderMarkingMap[item?.order_ids[0]].key,
                      ).backgroundColor,
                    },
                  ]}
                >
                  <Text
                    variant="body_text_4"
                    style={{
                      color: getTagStyle(
                        orderMarkingMap[item?.order_ids[0]].key,
                      ).textColor,
                    }}
                  >
                    {
                      getTagStyle(orderMarkingMap[item?.order_ids[0]].key)
                        .tagText
                    }
                  </Text>
                </View>
              )}
              {getTagStyle(
                orderMarkingMap[item?.order_ids[0]].key,
                item?.buttonsInfo?.length > 0,
              ).showMarkingChange &&
                changeMarkingEnabled && (
                  <TouchableOpacity
                    style={styles.markingBtn}
                    onPress={() => onChangeMarking(item?.order_ids[0])}
                  >
                    <Reload />
                    <Text
                      variant="caption_4"
                      style={{
                        color: color.black,
                        lineHeight: 18,
                        marginLeft: 6,
                      }}
                    >
                      {t(`${PATH}/change_marking`)}
                    </Text>
                  </TouchableOpacity>
                )}
            </View>
          ) : (
            <View style={styles.buttonContainer}>
              {item?.buttonsInfo?.map((button) => (
                <Button
                  key={button.key}
                  title={button.value}
                  disabled={cardMarkingBtnRef.current}
                  disabledColors={false}
                  backgroundColor={
                    button?.color === "primary"
                      ? "rgba(25, 129, 85, 1)"
                      : "rgba(0, 0, 0, 0.05)"
                  }
                  btnColor={
                    button?.color === "primary"
                      ? "rgba(25, 129, 85, 1)"
                      : "rgba(0, 0, 0, 0.05)"
                  }
                  textColor={
                    button?.color === "primary"
                      ? color.white
                      : color.mediumBlack
                  }
                  onPress={() => {
                    if (cardMarkingBtnRef.current) {
                      return;
                    }
                    onPressBtn(button.key, item);
                  }}
                />
              ))}
            </View>
          )}
        </View>
      )}
    </View>
  );
};

export default InnerDeliveryCard;

const styles = StyleSheet.create({
  bottomButtonContainer: {
    marginVertical: 16,
    paddingHorizontal: 10,
  },
  buttonContainer: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
  },
  listView: {
    backgroundColor: color.white,
    borderRadius: 10,
    elevation: 2,
    overflow: "hidden",
    marginHorizontal: 16,
    marginVertical: 8,
  },
  cardHeaderContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    paddingVertical: 16,
    paddingTop: 12,
  },
  divider: {
    height: 1,
    backgroundColor: color.lighterBlack,
    marginTop: 8,
  },
  dividerMargin: {
    marginTop: 12,
    marginHorizontal: 16,
  },
  markingBtn: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
  },
  issueBtn: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderTopRightRadius: 6,
    borderBottomRightRadius: 6,
    right: 12,
  },
  quantityContainer: {
    borderRadius: 12,
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    paddingHorizontal: 10,
    paddingVertical: 2,
  },
  quantityText: {
    color: color.mediumBlack,
  },
  titleText: {
    color: color.black06,
  },
  amountOverviewContainer: {
    marginTop: 16,
    flexDirection: "row",
    paddingHorizontal: 16,
    alignItems: "center",
  },
  amountCollectedContainer: {
    paddingHorizontal: 8,
  },
  amountLabel: {
    paddingHorizontal: 8,
    lineHeight: 26,
    color: color.mediumBlacklightBlack,
  },
  amountText: {
    color: color.black,
    lineHeight: 26,
  },
  imagesContainer: {
    paddingHorizontal: 16,
  },
  spacer12: {
    height: 12,
  },
});
